import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { setCachedConnectedCalendarsCount } from 'composables/useConnectedCalendarsCountCache.js'

const CACHE_TTL = 5 * 60 * 1000

export const useConnectedCalendarsStore = defineStore('connected_calendars', {
  state: () => ({
    calendars: [],
    fetchedAt: null,
    loading: false,
    error: null,
  }),

  getters: {
    isCacheValid: (state) =>
      state.fetchedAt !== null && Date.now() - state.fetchedAt < CACHE_TTL,
  },

  actions: {
    async fetchIfStale() {
      if (this.isCacheValid) return
      await this.fetchCalendars()
    },

    async fetchCalendars() {
      this.loading = true
      this.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', '/api/v1/connected_calendars', {}, false)
        if (!res.js_error && res.response.success) {
          this.calendars = res.response.data
          this.fetchedAt = Date.now()
          setCachedConnectedCalendarsCount(this.calendars.length)
        } else {
          this.error = 'Failed to load calendars.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    updateCalendar(id, patch) {
      const idx = this.calendars.findIndex((c) => c.id === id)
      if (idx !== -1) Object.assign(this.calendars[idx], patch)
      this.fetchedAt = Date.now()
    },

    updateSubCalendar(parentId, subId, patch) {
      const parent = this.calendars.find((c) => c.id === parentId)
      if (!parent?.sub_calendars) return
      const idx = parent.sub_calendars.findIndex((s) => s.id === subId)
      if (idx !== -1) Object.assign(parent.sub_calendars[idx], patch)
      this.fetchedAt = Date.now()
    },

    removeCalendar(id) {
      this.calendars = this.calendars.filter((c) => c.id !== id)
      this.fetchedAt = Date.now()
    },

    invalidate() {
      this.fetchedAt = null
    },
  },
})
